import { pathOr } from 'ramda';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import MiniProfileFormPanel from '../../MiniProfileFormPanel';
import moment from 'moment';
import {
  Box,
  Typography,
  Button,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';

import usePrimaryPortalStatusQuery from '../../../hooks/data-fetching/usePrimaryPortalStatusQuery';
import messages from './messages';

const PANEL_TITLE = 'Portal';

type Props = {
  isCommercial: boolean,
  isGuarantor: boolean,
  onClose: Function,
  openPanel: ?string,
  personalInfo: Object,
  residentPortalInfo: {
    display: boolean,
    sendPortalInviteEmail: Function,
    isAffordaPortal?: boolean,
  },
  specialNeedsAffordableChange: boolean,
  togglePanel: Function,
  application?: Object,
  selectedProperty?: Object,
  onResetSuccess?: Function,
};

const ResidentPortalDisabledErrorMessage = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography
        sx={{
          marginTop: 'calc(25% - 15px) !important',
          maxWidth: '300px',
          fontSize: '17px',
          lineHeight: 'inherit',
        }}
      >
        There is no information currently available for this household member.
      </Typography>
    </Box>
  );
};

export const ResidentPortalEmail = ({
  isCommercial,
  isGuarantor,
  onClose,
  openPanel,
  personalInfo,
  residentPortalInfo,
  specialNeedsAffordableChange,
  togglePanel,
  application,
  selectedProperty,
  onResetSuccess,
}: Props) => {
  const applicationId = application?.id ?? null;
  const isAffordaPortal = residentPortalInfo?.isAffordaPortal ?? false;
  const isPrimary = personalInfo?.isPrimary ?? false;
  const shouldFetchPrimaryStatus =
    residentPortalInfo?.display &&
    personalInfo?.type !== 'minor' &&
    isAffordaPortal &&
    !isPrimary &&
    !!applicationId;
  const { data: primaryStatus, isLoading: isPrimaryStatusLoading } =
    usePrimaryPortalStatusQuery(applicationId, {
      enabled: shouldFetchPrimaryStatus,
    });
  const primaryIsClaimed = primaryStatus?.primaryIsClaimed ?? false;
  const primaryHouseholdComplete =
    primaryStatus?.primaryHouseholdComplete ?? false;
  const isNonPrimaryDisabled =
    isAffordaPortal &&
    !isPrimary &&
    (isPrimaryStatusLoading || !primaryIsClaimed || !primaryHouseholdComplete);

  if (
    !residentPortalInfo ||
    !residentPortalInfo.display ||
    personalInfo?.type === 'minor'
  ) {
    if (isCommercial || (specialNeedsAffordableChange && !isGuarantor)) {
      return null;
    }

    return <ResidentPortalDisabledErrorMessage />;
  }

  const { customerStatusTitle } = personalInfo;

  const sendPortalInviteEmail = (id) => {
    residentPortalInfo.sendPortalInviteEmail(id);
    onClose();
  };
  const portalUser = pathOr({}, ['portalUser'], personalInfo);
  const idPath =
    customerStatusTitle === 'Prospect' ? 'prospectId' : 'customerId';
  const idForEmail = pathOr('', [idPath], personalInfo);
  const { claimRequestedAt, claimToken, isClaimed } = portalUser;
  const lastSent = claimRequestedAt ? moment(claimRequestedAt) : null;
  let statusMessage = '';
  if (isClaimed) {
    statusMessage = `${customerStatusTitle} is on ${PANEL_TITLE}`;
  } else if (!claimToken && !isClaimed) {
    statusMessage = `${customerStatusTitle} has not been invited to ${PANEL_TITLE}`;
  } else if (claimToken && !isClaimed) {
    statusMessage = `${customerStatusTitle} previously invited to ${PANEL_TITLE}`;
    if (lastSent) {
      statusMessage += ` on ${lastSent.format(
        'MMMM Do, YYYY',
      )} at ${lastSent.format('h:mm:ss A')}`;
    }
  }

  const emailAddress = pathOr(null, ['emailAddress'], personalInfo);
  const noEmailAddress =
    !emailAddress || emailAddress === 'batch_default@mail.com';
  const buttonDisabled = noEmailAddress || isNonPrimaryDisabled;
  const noEmailTooltipMessage = (
    <FormattedMessage
      {...messages.noEmailInviteDisabledTooltip}
      values={{
        customerStatusTitle,
        panelTitle: PANEL_TITLE,
      }}
    />
  );
  const tooltipMessage = (() => {
    if (isNonPrimaryDisabled && noEmailAddress) {
      return (
        <>
          <FormattedMessage {...messages.nonPrimaryInviteDisabledTooltip} />
          <br />
          {noEmailTooltipMessage}
        </>
      );
    }
    if (isNonPrimaryDisabled) {
      return <FormattedMessage {...messages.nonPrimaryInviteDisabledTooltip} />;
    }
    if (noEmailAddress) {
      return noEmailTooltipMessage;
    }
    return null;
  })();

  return (
    <MiniProfileFormPanel
      open={openPanel === PANEL_TITLE}
      toggleOpen={togglePanel(PANEL_TITLE)}
      title={PANEL_TITLE}
    >
      <div className="container-fluid padleft5 padright5 padtop15 padbottom15 text-center">
        <div
          style={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Tooltip
            title={tooltipMessage}
            containerSX={{ display: 'inline-block' }}
          >
            <Button
              variant="primary"
              onClick={() => sendPortalInviteEmail(idForEmail)}
              disabled={buttonDisabled}
              size="medium"
            >
              Send Portal Invite
            </Button>
          </Tooltip>
        </div>

        <div className="row">
          {noEmailAddress && (
            <div className="col-xs-12 text-center">
              <small className="text-gray--darker">
                <span>
                  <FormattedMessage
                    {...messages.noEmailInviteDisabledTooltip}
                    values={{
                      customerStatusTitle,
                      panelTitle: PANEL_TITLE,
                    }}
                  />
                </span>
              </small>
            </div>
          )}
          <div className="col-xs-12 text-center">
            <small className="text-gray--darker">
              <span>{statusMessage}</span>
            </small>
          </div>
        </div>
      </div>
    </MiniProfileFormPanel>
  );
};

export default ResidentPortalEmail;
